package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

// COPY RULE: no user-facing English here. Callers pass localized labels/hints; network names are
// trademarks supplied by the caller's data layer (identical across EN/FR/FA).

/** One pinned network on the [ShareSheet]: brand [label], official brand [badgeColor], tap action. */
data class ShareSheetTarget(
    val label: String,
    val badgeColor: Color,
    val onClick: () -> Unit,
)

/**
 * The app-wide share sheet: the community's networks first — a row of circular brand badges —
 * then utility rows for copy-link and the OS share sheet ("more"). Hosted on the single
 * [AppBottomSheet]. [footnote] is an optional localized caption under the network row (e.g. the
 * Instagram "link is copied — paste it in your story" hint). RTL-safe (start/end via Row/Arrangement).
 */
@Composable
fun ShareSheet(
    title: String,
    targets: List<ShareSheetTarget>,
    copyLinkLabel: String,
    onCopyLink: () -> Unit,
    moreLabel: String,
    onMore: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    footnote: String? = null,
) {
    AppBottomSheet(onDismissRequest = onDismiss, title = title, modifier = modifier) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = AppTheme.spacing.md, bottom = AppTheme.spacing.sm),
            horizontalArrangement = Arrangement.SpaceEvenly,
        ) {
            targets.forEach { target ->
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.xs),
                    modifier = Modifier.clickable(onClick = target.onClick),
                ) {
                    Box(
                        modifier = Modifier
                            .size(AppTheme.sizes.avatarMd)
                            .background(target.badgeColor, CircleShape),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            // The badge glyph is the brand's initial — recognizable together with
                            // the official brand color without shipping trademarked logo art.
                            text = target.label.take(1),
                            style = AppTheme.typography.headlineMd,
                            color = AppTheme.colors.onPrimary,
                        )
                    }
                    Text(
                        text = target.label,
                        style = AppTheme.typography.labelSm,
                        color = AppTheme.colors.onSurface,
                    )
                }
            }
        }
        if (footnote != null) {
            Text(
                text = footnote,
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth().padding(bottom = AppTheme.spacing.sm),
            )
        }
        UtilityRow(iconName = "link", label = copyLinkLabel, onClick = onCopyLink)
        UtilityRow(iconName = "share", label = moreLabel, onClick = onMore)
    }
}

@Composable
private fun UtilityRow(iconName: String, label: String, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
            .padding(vertical = AppTheme.spacing.md),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.md),
    ) {
        Box(
            modifier = Modifier
                .size(AppTheme.sizes.iconCircle)
                .background(AppTheme.colors.surfaceContainerHigh, CircleShape),
            contentAlignment = Alignment.Center,
        ) {
            AppIcon(name = iconName, size = AppTheme.sizes.iconSm, tint = AppTheme.colors.onSurface)
        }
        Text(text = label, style = AppTheme.typography.bodyLg, color = AppTheme.colors.onSurface)
    }
}

@Preview
@Composable
private fun ShareSheetPreview() {
    AppTheme {
        ShareSheet(
            title = "Share",
            targets = listOf(
                ShareSheetTarget("Telegram", AppTheme.colors.brandTelegram) {},
                ShareSheetTarget("WhatsApp", AppTheme.colors.brandWhatsApp) {},
                ShareSheetTarget("Instagram", AppTheme.colors.brandInstagram) {},
                ShareSheetTarget("Facebook", AppTheme.colors.brandFacebook) {},
            ),
            copyLinkLabel = "Copy link",
            onCopyLink = {},
            moreLabel = "More options",
            onMore = {},
            onDismiss = {},
        )
    }
}
